import React, { useState, useRef } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { Form, Modal } from 'react-bootstrap';
import { imageUrl, uploadImage } from '../../../helpers/api';
import { Client, Provider } from '../../../helpers/models';
import { useAlert } from 'react-alert';
import { monitoringCaptureError } from '../../../helpers/monitoring';
// import { Image } from './Image';
import clsx from 'clsx';

type Props = {
  title: string;
  size?: 'sm' | 'lg' | 'xl' | undefined;
  onCancel: () => void;
  onUpdate: (uuid: string | undefined) => void;
  show: boolean;
  rectangle?: boolean;
  extra: any;
  imageUuid?: string | undefined | null;
};

function resizeImagePreservingRatio(originalWidth: number, originalHeight: number, newHeight: number) {
  let newWidth = (originalWidth * newHeight) / originalHeight;
  return { width: Math.round(newWidth), height: newHeight };
}

const ImageUpload: React.FC<Props> = (props) => {
  const [image, setImage] = useState<File | null>(null);
  const [avatarEditorWidth, setAvatarEditorWidth] = useState<number | undefined>();
  const [avatarEditorHeight, setAvatarEditorHeight] = useState<number | undefined>();
  const [scale, setScale] = useState<number>(1);
  const editorRef = useRef<AvatarEditor>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const alert = useAlert();
  const maxHeight = 200;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setImage(e.target.files[0]);
      var img = new Image();
      img.onload = function () {
        if (props.rectangle) {
          const resized = resizeImagePreservingRatio(img.width, img.height, img.height < 100 ? 100 : 200);
          setAvatarEditorWidth(Math.min(resized.width, 800));
          setAvatarEditorHeight(resized.height);
        } else {
          setAvatarEditorWidth(200);
          setAvatarEditorHeight(200);
        }
      };
      img.src = URL.createObjectURL(e.target.files[0]);
    }
  };

  const handleSave = async () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      canvas.toBlob(async (blob) => {
        if (blob) {
          // await uploadAvatar(blob);
          uploadImage({ image: blob, filename: image?.name || '', ...props.extra })
            .then((res) => {
              if (res?.data?.uuid) {
                props.onUpdate(res.data.uuid);
              } else {
                alert.error('Failed to upload image');
                monitoringCaptureError(Error('Image upload failed to return uuid'), {
                  module: 'api',
                  func: 'ImageUpload.handleSave',
                  data: { extra: props.extra },
                });
              }
            })
            .catch((error) => {
              alert.error('Failed to upload image');
              monitoringCaptureError(error, {
                module: 'api',
                func: 'ImageUpload.handleSave',
                data: { extra: props.extra },
              });
            });
        }
      });
    }
  };

  const handleRemove = async () => {
    props.onUpdate(undefined);
  };

  return (
    <Modal
      animation={false}
      show={props.show}
      onHide={() => {
        props.onCancel();
      }}
      backdrop={true}
      centered
      size={props.size}
      dialogClassName={props.rectangle ? 'modal-900w' : undefined}
      // style={{ width: props.rectangle ? '850px' : undefined }}
    >
      <Modal.Header>
        <Modal.Title>{props.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <Form.Group controlId="formFile" className="my-3">
          <Form.Label>Image File</Form.Label>
          <Form.Control type="file" onChange={handleImageChange} />
        </Form.Group> */}

        {/* <input type="file" accept="image/*" onChange={handleImageChange} /> */}
        <div className="d-flex flex-column align-items-center">
          <div
            className="mt-5"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            <div
              style={
                props.rectangle
                  ? {
                      width: 850,
                      height: 250,
                      borderRadius: '3%',
                      overflow: 'hidden', // Ensures the editor content is clipped within the circular frame
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
                  : {
                      width: 250,
                      height: 250,
                      borderRadius: '50%',
                      overflow: 'hidden', // Ensures the editor content is clipped within the circular frame
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }
              }
            >
              {image && avatarEditorWidth && avatarEditorHeight ? (
                <>
                  <AvatarEditor
                    ref={editorRef}
                    image={image}
                    width={avatarEditorWidth}
                    height={avatarEditorHeight}
                    border={50}
                    scale={scale}
                    borderRadius={props.rectangle ? 3 : 100} // Set borderRadius to half of width/height for a circle
                  />
                </>
              ) : (
                <>
                  {props.imageUuid ? (
                    <div
                      className="d-flex justify-content-center"
                      style={{
                        position: 'relative',
                        width: !props.rectangle ? '200px' : undefined,
                        maxWidth: props.rectangle ? '800px' : undefined,
                        height: '200px',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        className="d-flex flex-column align-items-center"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          // color: '#FFFFFF',
                          // fontSize: '16px',
                          textShadow: '0 0 5px rgba(255, 255, 255, 0.8), 0 0 10px rgba(255, 255, 255, 0.5)',
                          // backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
                          // textAlign: 'center',
                        }}
                      >
                        <div
                          className="rounded-3 p-1 text-center"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
                        >
                          <div className="text-primary fs-6 fw-bold">Click to</div>
                          <div className="text-primary fs-3 fw-bold">Upload New Image</div>
                        </div>
                      </div>
                      <img
                        className={props.rectangle ? 'logo-img-rectangle' : 'avatar-img-round'}
                        src={imageUrl(props.imageUuid)}
                        style={{ width: '100%', height: '100%', maxWidth: '800px', objectFit: 'cover' }}
                      />
                    </div>
                  ) : (
                    <div
                      className={clsx('cursor-pointer', {
                        'avatar-circle': !props.rectangle,
                        'logo-rectangle': props.rectangle,
                      })}
                      // className="symbol symbol-circle symbol-100px symbol-fixed"
                      style={
                        props.rectangle ? { width: '800px', height: '200px' } : { width: '200px', height: '200px' }
                      }
                    >
                      <div className="d-flex flex-column align-items-center">
                        <div className="text-primary fs-6">Click to</div>
                        <div className="text-primary fs-3 mb-6">Upload Image</div>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </>
              )}
            </div>
          </div>
          <div className={clsx('mt-2', { 'opacity-25': image === null })}>
            <div className="text-center text-primary">Resize</div>
            <button
              className="btn btn-outline btn-outline-primary btn-icon w-30px h-30px me-3"
              onClick={() => {
                setScale(Math.max(scale - 0.1, 0.1));
              }}
              disabled={scale <= 0.1}
            >
              <i className="fas fa-search-minus inherit-color" />
            </button>
            <button
              className="btn btn-outline btn-outline-primary btn-icon w-30px h-30px"
              onClick={() => {
                setScale(Math.min(scale + 0.1, 10));
              }}
              disabled={scale >= 10}
            >
              <i className="fas fa-search-plus inherit-color" />
            </button>
          </div>
          <div className={clsx('mt-3 text-primary', { 'opacity-25': image === null })}>Drag image to reposition</div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {props.imageUuid && (
            <button className="btn btn-outline btn-outline-danger btn-sm" onClick={handleRemove}>
              Remove Image
            </button>
          )}
        </div>
        <div>
          <button
            className="btn btn-outline btn-outline-primary btn-sm me-3"
            onClick={() => {
              props.onCancel();
            }}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={!image}>
            Save
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageUpload;
